import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin } from 'lucide-react';
import { API_BASE } from '../config';

// The live, persisted "wall of latest updates" bell — every notifyEvent()
// fired across the portal (backend/shared/notify.js), filtered server-side
// (manager-alerts.js) to whatever this user has view access to (which edit
// access always implies). Distinct from components/NotificationBell.jsx,
// which is an unrelated personal "turn on push" opt-in pill.
//
// Mounted in two places: SidebarNext.jsx's user block (every domain layout)
// and Landing.jsx's previously-dead "coming soon" bell button.
const POLL_MS = 60000;

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const then = new Date(dateStr);
    if (isNaN(then)) return '';
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

export default function NotificationCenter({ user, className, buttonStyle, variant = 'icon', label = 'Notifications', panelStyle }) {
    const navigate = useNavigate();
    const [open, setOpen]     = useState(false);
    const [items, setItems]   = useState([]);
    const [loading, setLoading] = useState(true);
    const rootRef = useRef(null);

    const load = useCallback(async () => {
        if (!user?.username) return;
        try {
            const res  = await fetch(`${API_BASE}/manager-alerts?action=feed&username=${encodeURIComponent(user.username)}`);
            const data = await res.json();
            if (Array.isArray(data)) setItems(data);
        } catch (e) { console.error('Failed to load notifications', e); }
        finally { setLoading(false); }
    }, [user?.username]);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (!open) return;
        function onOutside(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); }
        function onEsc(e) { if (e.key === 'Escape') setOpen(false); }
        document.addEventListener('mousedown', onOutside);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onOutside);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open]);

    async function post(action, body) {
        try {
            await fetch(`${API_BASE}/manager-alerts?action=${action}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: user.username, ...body }),
            });
        } catch (e) { console.error(`Failed to ${action}`, e); }
    }

    function openItem(item) {
        if (!item.is_read) {
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_read: true } : i));
            post('mark_read', { id: item.id });
        }
        setOpen(false);
        if (item.url) navigate(item.url);
    }

    function toggleRead(e, item) {
        e.stopPropagation();
        const next = !item.is_read;
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_read: next } : i));
        post(next ? 'mark_read' : 'mark_unread', { id: item.id });
    }

    function dismiss(e, item) {
        e.stopPropagation();
        setItems(prev => prev.filter(i => i.id !== item.id));
        post('dismiss', { id: item.id });
    }

    function markAllRead() {
        const unreadIds = items.filter(i => !i.is_read).map(i => i.id);
        if (unreadIds.length === 0) return;
        setItems(prev => prev.map(i => ({ ...i, is_read: true })));
        post('mark_all_read', { ids: unreadIds });
    }

    function togglePin(e, item) {
        e.stopPropagation();
        const next = !item.is_pinned;
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_pinned: next } : i));
        post(next ? 'pin' : 'unpin', { id: item.id });
    }

    // Clears everything except pinned items — pinning exists precisely so a
    // priority item survives "Clear all" and still needs its own X to remove.
    function clearAll() {
        const idsToClear = items.filter(i => !i.is_pinned).map(i => i.id);
        if (idsToClear.length === 0) return;
        setItems(prev => prev.filter(i => i.is_pinned));
        post('clear_all', { ids: idsToClear });
    }

    if (!user?.username) return null;
    const unreadCount = items.filter(i => !i.is_read).length;
    const clearableCount = items.filter(i => !i.is_pinned).length;

    if (variant === 'inline') {
        return (
            <div style={{ ...S.inlineWrap, flex: 1, minHeight: 0 }}>
                <div style={S.inlineHeader}>
                    <span style={S.inlineHeaderTitle}>{label}</span>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {unreadCount > 0 && <span style={S.markAll} onClick={markAllRead}>Mark all read</span>}
                        {clearableCount > 0 && <span style={S.markAll} onClick={clearAll}>Clear all</span>}
                    </div>
                </div>
                <div style={S.inlineList}>
                    {loading ? (
                        <div style={S.empty}>Loading...</div>
                    ) : items.length === 0 ? (
                        <div style={S.empty}>No notifications yet</div>
                    ) : items.map(item => (
                        <div key={item.id} onClick={() => openItem(item)} style={{ ...S.row, background: item.is_pinned ? S.pinnedBg : item.is_read ? '#fff' : '#F8F6FC' }}>
                            <div onClick={e => toggleRead(e, item)} title={item.is_read ? 'Mark as unread' : 'Mark as read'} style={S.dotWrap}>
                                <span style={{ ...S.dot, background: item.is_read ? '#D8D2E5' : '#8C6FC0' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ ...S.title, fontWeight: item.is_read ? 500 : 700 }}>{item.title}</div>
                                {item.body && <div style={S.body}>{item.body}</div>}
                                <div style={S.time}>{timeAgo(item.created_at)}</div>
                            </div>
                            <button onClick={e => togglePin(e, item)} title={item.is_pinned ? 'Unpin' : 'Pin for later review'} style={{ ...S.pinBtn, color: item.is_pinned ? '#C0394B' : '#B7AFC7' }}>
                                <Pin size={14} fill={item.is_pinned ? '#C0394B' : 'none'} />
                            </button>
                            <button onClick={e => dismiss(e, item)} title="Remove" style={S.dismissBtn}>&times;</button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const bellIcon = (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    );

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            {variant === 'row' ? (
                <button
                    type="button"
                    className={className}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                >
                    <span className="sbn-icon" style={{ position: 'relative' }}>
                        {bellIcon}
                        {unreadCount > 0 && <span style={S.rowBadgeDot} />}
                    </span>
                    <span className="sbn-label sbn-collapsible">{label}</span>
                    {unreadCount > 0 && (
                        <span className="sbn-badge sbn-collapsible" aria-label={`${unreadCount} unread`}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                    <span className="sbn-tip">{label}{unreadCount > 0 ? ` — ${unreadCount} unread` : ''}</span>
                </button>
            ) : (
                <button
                    type="button"
                    className={className}
                    onClick={() => setOpen(o => !o)}
                    aria-label="Notifications"
                    aria-expanded={open}
                    title="Notifications"
                    style={{ position: 'relative', ...buttonStyle }}
                >
                    {bellIcon}
                    {unreadCount > 0 && (
                        <span style={S.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                    )}
                </button>
            )}

            {open && (
                <div style={{ ...S.panel, ...panelStyle }} role="menu">
                    <div style={S.header}>
                        <span style={S.headerTitle}>Notifications</span>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {unreadCount > 0 && <span style={S.markAll} onClick={markAllRead}>Mark all read</span>}
                            {clearableCount > 0 && <span style={S.markAll} onClick={clearAll}>Clear all</span>}
                        </div>
                    </div>
                    <div style={S.list}>
                        {loading ? (
                            <div style={S.empty}>Loading...</div>
                        ) : items.length === 0 ? (
                            <div style={S.empty}>No notifications yet</div>
                        ) : items.map(item => (
                            <div key={item.id} onClick={() => openItem(item)} style={{ ...S.row, background: item.is_pinned ? S.pinnedBg : item.is_read ? '#fff' : '#F8F6FC' }}>
                                <div onClick={e => toggleRead(e, item)} title={item.is_read ? 'Mark as unread' : 'Mark as read'} style={S.dotWrap}>
                                    <span style={{ ...S.dot, background: item.is_read ? '#D8D2E5' : '#8C6FC0' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ ...S.title, fontWeight: item.is_read ? 500 : 700 }}>{item.title}</div>
                                    {item.body && <div style={S.body}>{item.body}</div>}
                                    <div style={S.time}>{timeAgo(item.created_at)}</div>
                                </div>
                                <button onClick={e => togglePin(e, item)} title={item.is_pinned ? 'Unpin' : 'Pin for later review'} style={{ ...S.pinBtn, color: item.is_pinned ? '#C0394B' : '#B7AFC7' }}>
                                    <Pin size={14} fill={item.is_pinned ? '#C0394B' : 'none'} />
                                </button>
                                <button onClick={e => dismiss(e, item)} title="Remove" style={S.dismissBtn}>&times;</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    inlineWrap: { display: 'flex', flexDirection: 'column', fontFamily: "'Poppins', sans-serif" },
    inlineHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
    inlineHeaderTitle: { fontSize: 15, fontWeight: 500, color: '#231C30' },
    inlineList: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto', overflowX: 'auto', borderRadius: 12, border: '1px solid #F0ECF5' },
    rowBadgeDot: {
        position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: 999,
        background: '#C25E77', border: '1.5px solid var(--sbn-surface, #fff)',
    },
    badge: {
        position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16, padding: '0 4px',
        borderRadius: 999, background: '#C25E77', color: '#fff', fontSize: 10, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
    },
    panel: {
        position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 340, maxWidth: '90vw',
        background: '#fff', borderRadius: 14, border: '1px solid #ECE7F2', boxShadow: '0 12px 32px rgba(30,20,50,0.14)',
        zIndex: 100, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Poppins', sans-serif",
    },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #F1EEF6' },
    headerTitle: { fontSize: 14.5, fontWeight: 600, color: '#231C30' },
    markAll: { fontSize: 12, color: '#8C6FC0', fontWeight: 500, cursor: 'pointer' },
    list: { maxHeight: 420, overflowY: 'auto' },
    empty: { padding: '32px 16px', textAlign: 'center', color: '#9A90AE', fontSize: 13 },
    row: { display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid #F6F4FA', cursor: 'pointer', alignItems: 'flex-start' },
    dotWrap: { paddingTop: 5, flex: 'none', cursor: 'pointer' },
    dot: { display: 'block', width: 8, height: 8, borderRadius: 999 },
    title: { fontSize: 13, color: '#231C30', lineHeight: 1.35 },
    body: { fontSize: 12, color: '#6B6478', marginTop: 3, lineHeight: 1.4 },
    time: { fontSize: 10.5, color: '#A79EB6', marginTop: 5 },
    dismissBtn: {
        flex: 'none', border: 'none', background: 'transparent', color: '#B7AFC7', fontSize: 18,
        lineHeight: 1, cursor: 'pointer', padding: '0 2px',
    },
    pinBtn: {
        flex: 'none', border: 'none', background: 'transparent', fontSize: 14,
        lineHeight: 1, cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center',
    },
    pinnedBg: '#FBEAEC',
};
